import { Title, Group } from "@mantine/core";
import { useState, useEffect } from "react";
import { PageWithBreadcrumbs } from "../../components/layout/PageWithBreadcrumbs";
import { ContainerWhite } from "../../components/core";
import { AfaSelect, AfaCombobox, AfaTable } from "../../components/ui";
import { useNavigate } from "react-router-dom";

const transformDataToControlFormat = (tramesesData: any[]) => {
  const meses = [
    "gener",
    "febrer",
    "marc",
    "abril",
    "maig",
    "juny",
    "juliol",
    "agost",
    "setembre",
    "octubre",
    "novembre",
    "desembre",
  ];

  const periodicityMap: Record<string, string> = {
    AN: "Anual",
    SE: "Semestral",
    TRI: "Trimestral",
    ME: "Mensual",
    SP: "Sense periodicitat",
  };

  return tramesesData.map((tramesa: any) => {
    const periodicityStr =
      typeof tramesa.periodicity === "string" ? tramesa.periodicity : "";
    const periodicityMatch = periodicityStr.match(/Value\s*=\s*(\w+)/);
    const periodicityCode = periodicityMatch ? periodicityMatch[1] : "";
    const periodicitat = periodicityMap[periodicityCode] ?? "Desconegut";

    const hoy = new Date();

    const row: Record<string, string> = {
      reporting: tramesa.titulo || "",
      periodicitat,
      estat: "",
      gener: "",
      febrer: "",
      marc: "",
      abril: "",
      maig: "",
      juny: "",
      juliol: "",
      agost: "",
      setembre: "",
      octubre: "",
      novembre: "",
      desembre: "",
    };

    const estadosMeses: string[] = [];

    if (Array.isArray(tramesa.fechasLimite)) {
      tramesa.fechasLimite.forEach((fecha: any) => {
        const finPeriodo = new Date(fecha.finPeriodo);
        const mesIndex = finPeriodo.getMonth();
        if (mesIndex < 0 || mesIndex > 11) return;

        const fechaLimite = new Date(fecha.fechaLimiteEntrega);

        let estado = "Pendent";

        if (fecha.entregada === true) {
          estado = "Rebut";
        } else if (fecha.estado === "Tramès") {
          estado = "Enviat";
        } else if (fechaLimite < hoy) {
          estado = "No rebut";
        }

        estadosMeses.push(estado);

        const mesKey = meses[mesIndex] as keyof typeof row;
        row[mesKey] = `${fechaLimite.toLocaleDateString("ca-ES")}|${estado}`;
      });
    }

    if (estadosMeses.includes("No rebut")) {
      row.estat = "No rebut";
    } else if (estadosMeses.includes("Pendent")) {
      row.estat = "Pendent";
    } else if (estadosMeses.includes("Enviat")) {
      row.estat = "Enviat";
    } else if (estadosMeses.length > 0) {
      row.estat = "Rebut";
    } else {
      row.estat = "";
    }

    return row;
  });
};

export function TramesesQuadreControl(props: any) {
  const [any, setAny] = useState<string | null>(null);
  const [filial, setFilial] = useState<string | null>(null);
  const [filials, setFilials] = useState([] as any[]);
  const [years, setYears] = useState([] as any[]);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([] as any);
  const navigate = useNavigate();

  useEffect(() => {
    setAny(new Date().getFullYear().toString());
  }, []);

  useEffect(() => {
    setLoading(true);
    if (any != null && filial != null) {
      let filialParam = "filialId=" + filial;
      filialParam = filialParam + "&entityId=" + props.entity.lookupId;
      if (filial.toString().includes("entityId")) filialParam = filial;

      props.apiService
        .GetTramesesControl(filialParam, any)
        .then((response: any) => {
          const transformed = transformDataToControlFormat(response);
          setTableData(transformed);
          setLoading(false);
        })
        .catch((error: any) => {
          throw Error("An error has occurred: " + error);
        });
    } else {
      setTableData([]);
      setLoading(false);
    }
  }, [filial, any]);

  useEffect(() => {
    async function initializeFilials() {
      const fieldsFilial = await props.apiService.GetDataFromEntity(
        props.entity.lookupId,
      );
      console.log(fieldsFilial);

      const entitatOptions = props.user.user.entitats
        .filter((x: any) => x.lookupId === props.entity.lookupId)
        .map((e: any) => ({
          label: e.lookupValue,
          value: `entityId=${e.lookupId}`,
        }));

      const filialOptions = fieldsFilial.filials.map((f: any) => ({
        label: f.lookupValue,
        value: f.lookupId,
      }));

      setFilials([...entitatOptions, ...filialOptions]);
      const creationYear = new Date(
        fieldsFilial.entityCreationDate,
      ).getFullYear();
      const endYear =  new Date().getFullYear() + 1;

      const data = Array.from({ length: endYear - creationYear + 1 }, (_, i) =>
        (creationYear + i).toString(),
      );

      setYears(data);
    }
    initializeFilials();
  }, []);

  const meseBadgeConfig: any = {
    colorMap: {
      "No rebut": "red",
      Rebut: "green",
      Pendent: "yellow",
      Enviat: "yellow",
    },
    valueFormatter: () => "",
    colorExtractor: (value: string) => {
      if (!value) return undefined;
      const [, estado] = value.split("|");
      return estado;
    },
  };

  const handleClickMes = (value: string, row: any, month: number) => {
    let year = 0;
    if (typeof value === "string" && value.includes("|")) {
      const [fechaStr = ""] = value.split("|");
      if (fechaStr) {
        const [, , YYYY = ""] = fechaStr.split("/");
        if (YYYY) year = parseInt(YYYY);
      }
    }
    const reporting = row.reporting;
    const entidadfilial = filial;
    navigate(
      `/trameses/iniciar-nova?year=${year}&month=${month}&reporting=${encodeURIComponent(
        reporting,
      )}&filial=${entidadfilial}`,
    );
  };

  return (
    <PageWithBreadcrumbs>
      <Title order={1}>Quadre de Control de Trameses</Title>
      <ContainerWhite>
        <Group align="flex-start" wrap="nowrap" gap="md">
          <div style={{ flex: "0 0 calc(25% - 12px)" }}>
            <AfaSelect
              id="any"
              name="any"
              label="Exercici"
              placeholder="Selecciona any"
              data={years}
              value={any || ""}
              onChange={setAny}
            />
          </div>
          <div style={{ flex: "0 0 calc(25% - 12px)" }}>
            <AfaCombobox
              id="filial"
              name="filial"
              label="Entitat / Filial"
              placeholder="Selecciona"
              data={filials}
              value={filial || ""}
              onChange={setFilial}
            />
          </div>
        </Group>

        <div style={{ marginTop: "24px" }}>
          <AfaTable
            loading={loading}
            loadingMessage="Carregant quadre de control de trameses..."
            columns={[
              {
                key: "reporting",
                title: "Reporting",
                type: "text",
                width: 350,
              },
              {
                key: "periodicitat",
                title: "Periodicitat",
                type: "text",
                width: 80,
              },
              {
                key: "estat",
                title: "Estat",
                type: "badge",
                width: 100,
                badgeConfig: {
                  colorMap: {
                    "No rebut": "red",
                    Rebut: "green",
                    Pendent: "yellow",
                    Enviat: "yellow",
                  },
                },
              },
              {
                key: "gener",
                title: "Gener",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 1),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "febrer",
                title: "Febrer",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 2),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "marc",
                title: "Març",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 3),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "abril",
                title: "Abril",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 4),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "maig",
                title: "Maig",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 5),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "juny",
                title: "Juny",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 6),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "juliol",
                title: "Juliol",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 7),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "agost",
                title: "Agost",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 8),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "setembre",
                title: "Setembre",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 9),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "octubre",
                title: "Octubre",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 10),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "novembre",
                title: "Novembre",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 11),
                  ...meseBadgeConfig,
                },
              },
              {
                key: "desembre",
                title: "Desembre",
                type: "badge",
                width: 60,
                badgeConfig: {
                  onClick: (v: any, r: any) => handleClickMes(v, r, 12),
                  ...meseBadgeConfig,
                },
              },
            ]}
            data={tableData}
            emptyState={{
              title: "No hi ha dades disponibles per al quadre de control.",
              description:
                "Selecciona un any i filial per veure les dades del quadre de control.",
            }}
            pagination={{}}
          />
        </div>
      </ContainerWhite>
    </PageWithBreadcrumbs>
  );
}
